if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        // Track which option was changed (for size auto-selection)
        const changedOption = this.getOptionFromTarget(target);
        this.lastChangedOption = changedOption;

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      getOptionFromTarget(target) {
        // Find the fieldset or select that contains this target
        const fieldset = target.closest('fieldset');
        const select = target.closest('select');
        
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            // Get the option name from legend, removing the selected value display
            // e.g., "FLAVOR: Ethiopian" -> "flavor"
            let optionName = legend.textContent.trim();
            // Remove everything after the colon (selected value)
            if (optionName.includes(':')) {
              optionName = optionName.split(':')[0].trim();
            }
            return optionName.toLowerCase();
          }
        } else if (select) {
          const label = select.closest('.product-form__input')?.querySelector('label');
          if (label) {
            return label.textContent.trim().toLowerCase();
          }
        }
        return null;
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          this.preProcessHtmlCallbacks.forEach((callback) => callback(html.querySelector('product-info')));
          const variant = this.getSelectedVariant(html);

          // Check for unavailable variant BEFORE updating the DOM
          if (!variant) {
            // Use the updated HTML to check availability (before DOM is updated)
            const variantSelectsFromHtml = html.querySelector('variant-selects');
            
            // If flavor was changed, try to find next available SIZE
            if (this.lastChangedOption && this.isFlavorOption(this.lastChangedOption)) {
              const nextAvailableSize = this.findNextAvailableSizeForFlavor(variantSelectsFromHtml);
              
              if (nextAvailableSize) {
                // Update the DOM first
                this.updateOptionValues(html);
                // Then select the size after a short delay
                setTimeout(() => {
                  const sizeOptionElement = this.findSizeOptionElement(this.variantSelectors);
                  if (sizeOptionElement) {
                    const updatedSize = this.findOptionByValue(sizeOptionElement, nextAvailableSize.value);
                    if (updatedSize) {
                      this.selectOptionValue(updatedSize);
                    }
                  }
                }, 100);
                return; // Will trigger another update when size is selected
              } else {
                // Show unavailable state if no size works
                this.pickupAvailability?.update(variant);
                this.updateOptionValues(html);
                this.updateURL(productUrl, variant?.id);
                this.updateVariantInputs(variant?.id);
                this.setUnavailable();
                return;
              }
            }
            
            // If size was changed, try to find next available SIZE (size up/down)
            if (this.lastChangedOption && this.isSizeOption(this.lastChangedOption)) {
              const nextAvailableSize = this.findNextAvailableOption(this.lastChangedOption, variantSelectsFromHtml);
              if (nextAvailableSize) {
                // Update the DOM first so we can find the element to select
                this.updateOptionValues(html);
                setTimeout(() => {
                  const updatedOption = this.findOptionByValue(
                    this.findOptionElement(this.variantSelectors, this.lastChangedOption),
                    nextAvailableSize.value
                  );
                  if (updatedOption) {
                    this.selectOptionValue(updatedOption);
                  }
                }, 100);
                return; // Will trigger another update
              }
            }
            
            // Update DOM before showing unavailable state
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);
            this.setUnavailable();
            return;
          }

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.dataset.section}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('subscriptions');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.dataset.section, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.dataset.section}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.preProcessHtmlCallbacks.forEach((callback) => callback(html.querySelector('product-info')));
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      isFlavorOption(optionName) {
        const flavorKeywords = ['flavor', 'flavors', 'flavour', 'flavours'];
        return flavorKeywords.some(keyword => optionName.includes(keyword));
      }

      isSizeOption(optionName) {
        const sizeKeywords = ['size', 'sizes', 'pack size', 'pack sizes'];
        return sizeKeywords.some(keyword => optionName.includes(keyword));
      }

      findNextAvailableSizeForFlavor(variantSelectsFromHtml) {
        // Use current DOM (after update) if variantSelectsFromHtml is null
        const variantSelects = variantSelectsFromHtml || this.variantSelectors;
        
        const sizeOption = this.findSizeOptionElement(variantSelects);
        if (!sizeOption) {
          return null;
        }

        // Get all sizes and their availability
        const sizes = this.getOptionValues(sizeOption);
        if (sizes.length === 0) {
          return null;
        }

        // Get currently selected size
        const currentSize = this.getCurrentSelectedOption(sizeOption);
        if (!currentSize) {
          return null;
        }

        // Find current size index
        const currentIndex = sizes.findIndex(s => s.value === currentSize.value);
        if (currentIndex === -1) {
          return null;
        }

        const availableSizes = sizes.filter(s => s.available);
        if (availableSizes.length === 0) {
          return null;
        }

        // Try size up first (larger sizes)
        for (let i = currentIndex + 1; i < sizes.length; i++) {
          if (sizes[i].available) {
            return { value: sizes[i].value };
          }
        }

        // If no size up available, try size down (smaller sizes)
        for (let i = currentIndex - 1; i >= 0; i--) {
          if (sizes[i].available) {
            return { value: sizes[i].value };
          }
        }

        return null;
      }

      findNextAvailableOption(optionName, variantSelectsFromHtml = null) {
        // Use HTML response to check availability, but find elements in current DOM for selection
        const htmlSelects = variantSelectsFromHtml || this.variantSelectors;
        const currentSelects = this.variantSelectors;
        
        if (!htmlSelects || !currentSelects) return null;

        // Find the option element in HTML (for availability check)
        const htmlOptionElement = this.findOptionElement(htmlSelects, optionName);
        if (!htmlOptionElement) return null;

        // Find the corresponding option element in current DOM (for selection)
        const currentOptionElement = this.findOptionElement(currentSelects, optionName);
        if (!currentOptionElement) return null;

        // Get all option values and their availability from HTML
        const htmlOptions = this.getOptionValues(htmlOptionElement);
        if (htmlOptions.length === 0) return null;

        // Get currently selected option value from HTML (since that's what we're checking against)
        const currentOption = this.getCurrentSelectedOption(htmlOptionElement);
        if (!currentOption) return null;

        // Find current option index
        const currentIndex = htmlOptions.findIndex(o => o.value === currentOption.value);
        if (currentIndex === -1) return null;

        // Check if this is a size option (for size up logic)
        const isSizeOption = this.isSizeOption(optionName);

        let nextAvailableValue = null;
        if (isSizeOption) {
          // For size options: Try size up first (larger sizes), then size down
          for (let i = currentIndex + 1; i < htmlOptions.length; i++) {
            if (htmlOptions[i].available) {
              nextAvailableValue = htmlOptions[i].value;
              break;
            }
          }
          // If no size up available, try size down (smaller sizes)
          if (!nextAvailableValue) {
            for (let i = currentIndex - 1; i >= 0; i--) {
              if (htmlOptions[i].available) {
                nextAvailableValue = htmlOptions[i].value;
                break;
              }
            }
          }
        } else {
          // For flavor/other options: Try next option first, then previous
          for (let i = currentIndex + 1; i < htmlOptions.length; i++) {
            if (htmlOptions[i].available) {
              nextAvailableValue = htmlOptions[i].value;
              break;
            }
          }
          // If no next option available, try previous
          if (!nextAvailableValue) {
            for (let i = currentIndex - 1; i >= 0; i--) {
              if (htmlOptions[i].available) {
                nextAvailableValue = htmlOptions[i].value;
                break;
              }
            }
          }
        }

        if (!nextAvailableValue) return null;

        // Find the corresponding option in current DOM
        return this.findOptionByValue(currentOptionElement, nextAvailableValue);
      }

      findSizeOptionElement(variantSelects) {
        if (!variantSelects) return null;
        // DELETE AFTER TEST
        const activeTestId = document.body.dataset.pickerOptionsAbTestIdActive;
        // Try to find by legend text (for fieldsets)
        const fieldsets = variantSelects.querySelectorAll('fieldset');
        for (const fieldset of fieldsets) {
          // DELETE AFTER TEST
          const abContainer = fieldset.closest('[data-picker-options-ab-test-id]');
          if (abContainer && activeTestId && abContainer.dataset.pickerOptionsAbTestId !== activeTestId) {
            continue;
          }
          const legend = fieldset.querySelector('legend');
          if (legend) {
            const legendText = legend.textContent.trim().replace(':', '').toLowerCase();
            if (this.isSizeOption(legendText)) {
              return fieldset;
            }
          }
        }

        // Try to find by label text (for selects)
        const selects = variantSelects.querySelectorAll('select');
        for (const select of selects) {
          // DELETE AFTER TEST
          const abContainer = select.closest('[data-picker-options-ab-test-id]');
          if (abContainer && activeTestId && abContainer.dataset.pickerOptionsAbTestId !== activeTestId) {
            continue;
          }
          const label = select.closest('.product-form__input')?.querySelector('label');
          if (label) {
            const labelText = label.textContent.trim().toLowerCase();
            if (this.isSizeOption(labelText)) {
              return select;
            }
          }
        }

        return null;
      }

      findOptionElement(variantSelects, optionName) {
        // Normalize option name for comparison - remove common words and normalize
        const normalizedName = optionName.toLowerCase().trim().replace(/[:\s]+/g, ' ');
        
        // DELETE AFTER TEST
        const activeTestId = document.body.dataset.pickerOptionsAbTestIdActive;
        // Try to find by legend text (for fieldsets)
        const fieldsets = variantSelects.querySelectorAll('fieldset');
        for (const fieldset of fieldsets) {
          // DELETE AFTER TEST
          const abContainer = fieldset.closest('[data-picker-options-ab-test-id]');
          if (abContainer && activeTestId && abContainer.dataset.pickerOptionsAbTestId !== activeTestId) {
            continue;
          }
          const legend = fieldset.querySelector('legend');
          if (legend) {
            let legendText = legend.textContent.trim().replace(':', '').toLowerCase().replace(/[:\s]+/g, ' ');
            // Remove the selected value display if present (e.g., "Size: 48 Count" -> "size")
            legendText = legendText.split(':')[0].trim();
            // Check for exact match or contains match
            if (legendText === normalizedName || 
                legendText.includes(normalizedName) || 
                normalizedName.includes(legendText) ||
                this.optionNamesMatch(legendText, normalizedName)) {
              return fieldset;
            }
          }
        }

        // Try to find by label text (for selects)
        const selects = variantSelects.querySelectorAll('select');
        for (const select of selects) {
          // DELETE AFTER TEST
          const abContainer = select.closest('[data-picker-options-ab-test-id]');
          if (abContainer && activeTestId && abContainer.dataset.pickerOptionsAbTestId !== activeTestId) {
            continue;
          }
          const label = select.closest('.product-form__input')?.querySelector('label');
          if (label) {
            const labelText = label.textContent.trim().toLowerCase().replace(/[:\s]+/g, ' ');
            if (labelText === normalizedName || 
                labelText.includes(normalizedName) || 
                normalizedName.includes(labelText) ||
                this.optionNamesMatch(labelText, normalizedName)) {
              return select;
            }
          }
        }

        return null;
      }

      optionNamesMatch(name1, name2) {
        // Check if option names match (handling variations like "pack size" vs "size")
        const keywords1 = name1.split(/\s+/).filter(w => w.length > 2);
        const keywords2 = name2.split(/\s+/).filter(w => w.length > 2);
        
        // If they share significant keywords, consider them a match
        return keywords1.some(k => keywords2.includes(k)) || 
               keywords2.some(k => keywords1.includes(k));
      }

      getOptionValues(optionElement) {
        const options = [];
        
        if (optionElement.tagName === 'FIELDSET') {
          // Handle radio inputs (swatch or button)
          const inputs = optionElement.querySelectorAll('input[type="radio"]');
          // DELETE AFTER TEST
          const activeTestId = document.body.dataset.pickerOptionsAbTestIdActive;
          inputs.forEach(input => {
            // DELETE AFTER TEST
            const abContainer = input.closest('[data-picker-options-ab-test-id]');
            if (abContainer && activeTestId && abContainer.dataset.pickerOptionsAbTestId !== activeTestId) {
              return;
            }
            const label = optionElement.querySelector(`label[for="${input.id}"]`);
            const value = input.value;
            
            // The key indicator of availability is the 'disabled' class on the input
            // The label-unavailable span is always present (it's visually-hidden), so we ignore it
            // If the input doesn't have 'disabled' class and isn't actually disabled, it's available
            const hasDisabledClass = input.classList.contains('disabled');
            const hasVisuallyDisabledClass = input.classList.contains('visually-disabled');
            const isActuallyDisabled = input.disabled;
            
            // Available if NOT disabled (by class or attribute) and NOT visually-disabled
            const available = !hasDisabledClass && 
                            !hasVisuallyDisabledClass &&
                            !isActuallyDisabled;
            
            options.push({
              value: value,
              available: available,
              input: input,
              label: label
            });
          });
        } else if (optionElement.tagName === 'SELECT') {
          // Handle select dropdown
          const selectOptions = optionElement.querySelectorAll('option');
          selectOptions.forEach(option => {
            const value = option.value;
            // Check if option text indicates unavailable
            const text = option.textContent.trim();
            const available = !text.toLowerCase().includes('unavailable') && 
                            !option.hasAttribute('disabled') &&
                            !option.classList.contains('disabled');
            
            options.push({
              value: value,
              available: available,
              option: option
            });
          });
        }

        return options;
      }

      getCurrentSelectedOption(optionElement) {
        if (optionElement.tagName === 'FIELDSET') {
          const activeTestId = document.body.dataset.pickerOptionsAbTestIdActive;
          const checkedInputs = optionElement.querySelectorAll('input[type="radio"]:checked');
          let checkedInput = null;
          
          for (const input of checkedInputs) {
            const abContainer = input.closest('[data-picker-options-ab-test-id]');
            if (!abContainer || !activeTestId || abContainer.dataset.pickerOptionsAbTestId === activeTestId) {
              checkedInput = input;
              break;
            }
          }
          
          if (checkedInput) {
            return {
              value: checkedInput.value,
              input: checkedInput
            };
          }
        } else if (optionElement.tagName === 'SELECT') {
          const selectedOption = optionElement.selectedOptions[0];
          if (selectedOption) {
            return {
              value: selectedOption.value,
              option: selectedOption
            };
          }
        }
        return null;
      }

      findOptionByValue(optionElement, value) {
        if (!optionElement) return null;
        
        if (optionElement.tagName === 'FIELDSET') {
          const activeTestId = document.body.dataset.pickerOptionsAbTestIdActive;
          const inputs = optionElement.querySelectorAll(`input[type="radio"][value="${CSS.escape(value)}"]`);
          let targetInput = null;

          for (const input of inputs) {
            const abContainer = input.closest('[data-picker-options-ab-test-id]');
            if (!abContainer || !activeTestId || abContainer.dataset.pickerOptionsAbTestId === activeTestId) {
              targetInput = input;
              break;
            }
          }

          if (targetInput) {
            return {
              value: value,
              input: targetInput
            };
          }
        } else if (optionElement.tagName === 'SELECT') {
          const option = optionElement.querySelector(`option[value="${CSS.escape(value)}"]`);
          if (option) {
            return {
              value: value,
              option: option
            };
          }
        }
        return null;
      }

      selectOptionValue(optionValue) {
        if (optionValue.input) {
          // Select radio input
          optionValue.input.checked = true;
          // Trigger both change and input events to ensure all listeners fire
          optionValue.input.dispatchEvent(new Event('change', { bubbles: true }));
          optionValue.input.dispatchEvent(new Event('input', { bubbles: true }));
          // Also click the label if it exists to ensure visual update
          const label = document.querySelector(`label[for="${optionValue.input.id}"]`);
          if (label) {
            label.click();
          }
        } else if (optionValue.option) {
          // Select dropdown option
          const select = optionValue.option.closest('select');
          select.value = optionValue.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }
  );
}
